import { PaymentModuleId } from '@foundation/apis/payments';

import { ICustomer } from '@/schemas/Customer';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import { StartupAuthContext } from '@/services/base/Startupable';
import { getCustomerInformation } from '@/services/customer/CustomerApi';
import { isSepaMandatesEnabled } from '@/utils/env';
import { getWindowParam } from '@/utils/url';

import { type IManagePaymentInstrument } from './MandateReference';
import { getManagePaymentInstruments } from './PaymentInstrumentsApi';
import { IBraintreeClientTokens } from './PaymentInstrumentsApiResponse';
import { getInstrumentsConfig } from './PaymentInstrumentsConfigApi';
import { parsePaymentMethods } from '../intent/helpers/PaymentIntentHelper';

export class PaymentInstrumentsManageStore {
  public readonly name = 'PaymentInstrumentsManageStore';

  private _instruments: ReadonlyArray<IManagePaymentInstrument> = [];
  private _allowedPaymentModules: ReadonlyArray<PaymentModuleId> = [];
  private _braintreeClientTokens: IBraintreeClientTokens | undefined;
  private _currency = '';
  private _redirectUrl: string | undefined;
  private _skip3DS: boolean | undefined;
  private _customer: ICustomer | undefined;

  get flow(): PaymentIntentFlow {
    return PaymentIntentFlow.Manage;
  }

  public async init(authContext?: StartupAuthContext): Promise<void> {
    const customerNumber = authContext?.customerNumber;
    const accessToken = authContext?.accessToken ?? '';
    const legalEntityParam = getWindowParam('le');
    const legalEntityNumber =
      legalEntityParam !== undefined ? Number(legalEntityParam) : undefined;

    if (
      legalEntityNumber !== undefined &&
      (!Number.isInteger(legalEntityNumber) || legalEntityNumber <= 0)
    ) {
      throw new Error('Invalid legal entity');
    }

    if (customerNumber) {
      const [instruments, customer] = await Promise.all([
        getManagePaymentInstruments(
          customerNumber,
          accessToken,
          legalEntityNumber,
        ),
        getCustomerInformation(customerNumber, accessToken),
      ]);

      this._instruments = instruments;
      this._customer = customer;
    }

    const refererUrl = getWindowParam('return');
    if (refererUrl) {
      this._redirectUrl = decodeURI(refererUrl);
    }

    if (legalEntityNumber) {
      const newInstrumentConfig = await getInstrumentsConfig(
        legalEntityNumber,
        accessToken,
      );

      const allowedPaymentModules = parsePaymentMethods(
        newInstrumentConfig.paymentMethods,
      );
      // The backend strips direct debit from paymentMethods and reports SEPA separately.
      this._allowedPaymentModules =
        isSepaMandatesEnabled() &&
        newInstrumentConfig.sepaMandate?.available &&
        !allowedPaymentModules.includes(PaymentModuleId.Sepa)
          ? [...allowedPaymentModules, PaymentModuleId.Sepa]
          : allowedPaymentModules;
      this._braintreeClientTokens = newInstrumentConfig.braintreeClientTokens;
      this._currency = newInstrumentConfig.currency;
      this._skip3DS = newInstrumentConfig.skip3DS;
    }
  }

  get customerRegion() {
    return this._customer?.region ?? '';
  }

  get customerCity() {
    return this._customer?.city ?? '';
  }

  get customerZipCode() {
    return this._customer?.zipCode ?? '';
  }

  get customerStreet() {
    return this._customer?.street ?? '';
  }

  get customerCountry() {
    return this._customer?.countryCode ?? '';
  }

  get customerEmail() {
    return this._customer?.email ?? '';
  }

  get customerName() {
    const firstName = this._customer?.firstName;
    const lastName = this._customer?.lastName;

    return `${firstName} ${lastName}`.trim();
  }

  get instruments() {
    return this._instruments;
  }

  get allowedPaymentModules() {
    return this._allowedPaymentModules;
  }

  get braintreeClientTokens() {
    return this._braintreeClientTokens;
  }

  get currency() {
    return this._currency;
  }

  get redirectUrl() {
    return this._redirectUrl;
  }

  get skip3DS() {
    return this._skip3DS;
  }

  public forceUpdateAllowedPaymentMethods(
    runningModules: ReadonlyArray<PaymentModuleId>,
  ) {
    this._allowedPaymentModules = this._allowedPaymentModules.filter(
      (allowedMethodByApi) => runningModules.includes(allowedMethodByApi),
    );
  }
}
